import React from 'react'
import { css } from 'linaria'
import PageWrap from '../components/Layouts/PageWrap'
import Hero from '../components/Headers/Hero'
import Offset from '../components/Layouts/Offset'
import SectionContainer from '../components/Layouts/SectionContainer'
import ImageRounded from '../components/Images/ImageRounded'
import MediaText from '../components/MediaText'
import Image from '../components/Images/Image'
import CardInfoGroup from '../components/Cards/CardInfoGroup'
import WYSIWYG from '../components/WYSIWYG'
import { graphql } from 'gatsby'

const mapDescription = css`
  @screen lg {
    top: -160px;
  }
`

const transformData = ({ title, acfAbout }) => {
  const data = {}
  const {
    heroCopy,
    heroImage,
    featuredSection,
    experienceSection,
    distributedSection,
    partnersSection,
    certificationsSection,
    footerContent,
  } = acfAbout

  data.hero = {
    heading: heroCopy || '',
    sectionHeader: {
      heading: title,
      as: 'h1',
    },
  }

  if (heroImage) {
    data.heroImage = {
      src: heroImage.mediaItemUrl,
      alt: heroImage.altText,
    }
  }

  if (featuredSection) {
    data.featuredSection = {
      text: {
        sectionHeader: {
          heading: featuredSection.heading || '',
        },
        copy: featuredSection.copy || '',
      },
    }
    if (featuredSection.image) {
      data.featuredSection.image = {
        src: featuredSection.image.mediaItemUrl,
        alt: featuredSection.image.altText,
      }
    }
  }

  if (experienceSection) {
    data.experienceSection = {
      isOnDarkBg: true,
      sectionHeader: {
        heading: experienceSection.heading,
        subheading: experienceSection.subheading,
      },
      cards: [],
    }
    if (experienceSection.cards && experienceSection.cards.length) {
      data.experienceSection.cards = experienceSection.cards.map(
        ({ heading, image }) => {
          return {
            heading,
            image: image
              ? { alt: image.altText, src: image.mediaItemUrl }
              : null,
          }
        }
      )
    }
  }

  if (distributedSection) {
    data.distributedSection = {
      heading: distributedSection.heading || '',
      content: distributedSection.content || '',
      image: distributedSection.image
        ? {
            src: distributedSection.image.mediaItemUrl,
            alt: distributedSection.image.altText,
          }
        : null,
    }
  }

  if (partnersSection) {
    data.partnersSection = {
      isOnDarkBg: true,
      isTwoUp: true,
      sectionHeader: {
        heading: partnersSection.heading || '',
        subheading: partnersSection.subheading || '',
      },
      cards: [],
    }
    if (partnersSection.partners) {
      data.partnersSection.cards = partnersSection.partners.map(
        ({ copy, image, link }) => ({
          logoHasLeftBorder: true,
          subheading: copy || '',
          image: image
            ? {
                src: image.mediaItemUrl,
                alt: image.altText,
              }
            : null,
          link,
        })
      )
    }
  }

  if (certificationsSection) {
    data.certificationsSection = {
      isOnDarkBg: true,
      isTwoUp: true,
      sectionHeader: {
        heading: certificationsSection.heading || '',
        subheading: certificationsSection.subheading || '',
      },
      cards: [],
    }
    if (certificationsSection.certifications) {
      data.certificationsSection.cards = certificationsSection.certifications.map(
        ({ copy, image, link }) => ({
          logoHasLeftBorder: true,
          subheading: copy || '',
          image: image
            ? {
                src: image.mediaItemUrl,
                alt: image.altText,
              }
            : null,
          link,
        })
      )
    }
  }

  if (footerContent) {
    data.footerContent = {
      text: {
        sectionHeader: {
          heading: footerContent.heading || '',
        },
        copy: footerContent.copy || '',
        link: footerContent.link && {
          to: footerContent.link.url || '',
          label: footerContent.link.title || '',
        },
      },
      image:
        footerContent.image && footerContent.image.mediaItemUrl
          ? {
              src: footerContent.image.mediaItemUrl,
              alt: footerContent.image.altText || '',
            }
          : null,
    }
  }

  return data
}

const About = args => {
  const data = transformData(args.data.page)
  return (
    <PageWrap seo={args.data?.page?.seo} noFlex>
      <SectionContainer>
        <Hero size200 {...data.hero} />
      </SectionContainer>
      {data.heroImage && (
        <SectionContainer as="div">
          <Offset>
            <ImageRounded {...data.heroImage} />
          </Offset>
        </SectionContainer>
      )}
      {data.featuredSection && (
        <SectionContainer>
          <Offset direction="left" rounded={false}>
            <MediaText isImageRounded reverse {...data.featuredSection} />
          </Offset>
        </SectionContainer>
      )}
      {data.experienceSection && (
        <SectionContainer>
          <Offset direction="left">
            <div className="bg-gray-700 py-24 pr-10 w-full">
              <Offset direction="right">
                <CardInfoGroup
                  hasBg
                  {...data.experienceSection}
                ></CardInfoGroup>
              </Offset>
            </div>
          </Offset>
        </SectionContainer>
      )}

      {data.distributedSection && (
        <SectionContainer className="container">
          {data.distributedSection.heading && (
            <Hero size100 heading={data.distributedSection.heading} />
          )}
          {data.distributedSection.image && (
            <div className="my-16">
              <Image {...data.distributedSection.image} />
            </div>
          )}
          {data.distributedSection.content && (
            <div className={`${mapDescription} lg:w-2/5 lg:-mt-32`}>
              <WYSIWYG content={data.distributedSection.content} />
            </div>
          )}
        </SectionContainer>
      )}
      {data.partnersSection && (
        <SectionContainer>
          <Offset>
            <div className="bg-gray-700 py-24">
              <div className="container">
                <CardInfoGroup {...data.partnersSection}></CardInfoGroup>
              </div>
              {data.certificationsSection && (
                <div className="container pt-24">
                  <CardInfoGroup
                    {...data.certificationsSection}
                  ></CardInfoGroup>
                </div>
              )}
            </div>
          </Offset>
        </SectionContainer>
      )}
      {data.footerContent && (
        <SectionContainer>
          <Offset rounded={false}>
            <MediaText isImageRounded {...data.footerContent} />
          </Offset>
        </SectionContainer>
      )}
    </PageWrap>
  )
}

export default About

export const query = graphql`
  query($id: String) {
    page: wpPage(id: { eq: $id }) {
      seo {
        ...seoPostTypeFields
      }
      id
      title
      acfAbout {
        heroCopy
        heroImage {
          altText
          mediaItemUrl
        }
        featuredSection {
          copy
          heading
          image {
            altText
            mediaItemUrl
          }
        }
        experienceSection {
          heading
          subheading
          cards {
            heading
            image {
              altText
              mediaItemUrl
            }
          }
        }
        distributedSection {
          heading
          content
          image {
            altText
            mediaItemUrl
          }
        }
        partnersSection {
          heading
          subheading
          partners {
            copy
            image {
              altText
              mediaItemUrl
            }
            link
          }
        }
        certificationsSection {
          heading
          subheading
          certifications {
            copy
            image {
              altText
              mediaItemUrl
            }
            link
          }
        }
        footerContent {
          heading
          copy
          link {
            target
            title
            url
          }
          image {
            altText
            mediaItemUrl
          }
        }
      }
    }
  }
`
